import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { brandAssets } from "@/constants/brand";

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, statusMessage, setStatusMessage, loading } = useAuth();
  const [mode, setMode] = useState("login");
  const [formState, setFormState] = useState({
    email: "",
    password: "",
    username: "",
  });

  const handleChange = (event) => {
    setFormState((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setStatusMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const returnTo = searchParams.get("return");
    const safeReturn = returnTo && returnTo.startsWith("/") ? returnTo : "/dashboard";
    if (mode === "login") {
      const result = await login({ email: formState.email, password: formState.password });
      if (result.success) {
        navigate(safeReturn);
      }
      return;
    }
    const result = await register({
      email: formState.email,
      password: formState.password,
      username: formState.username,
    });
    if (result.success) {
      navigate(safeReturn);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16" data-testid="auth-page">
      <div className="panel-iron rounded-2xl p-10 w-full max-w-xl">
        <div className="flex items-center gap-4" data-testid="auth-header">
          <img
            src={brandAssets.logo}
            alt="Dracon Gaming"
            className="h-16 w-16 rounded-full border border-red-900/50 object-cover"
            data-testid="auth-logo"
          />
          <div>
            <p className="font-cinzel text-2xl" data-testid="auth-title">
              Dracon Sanctum
            </p>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid="auth-subtitle">
              Secure the ritual gate
            </p>
          </div>
        </div>

        <div className="flex gap-4 mt-8" data-testid="auth-mode-toggle">
          <Button
            type="button"
            onClick={() => switchMode("login")}
            className={
              mode === "login"
                ? "bg-red-900 hover:bg-red-800 text-stone-100"
                : "bg-stone-900 text-stone-400 hover:text-stone-100"
            }
            data-testid="auth-login-mode-button"
          >
            Return
          </Button>
          <Button
            type="button"
            onClick={() => switchMode("register")}
            className={
              mode === "register"
                ? "bg-red-900 hover:bg-red-800 text-stone-100"
                : "bg-stone-900 text-stone-400 hover:text-stone-100"
            }
            data-testid="auth-register-mode-button"
          >
            Bind Soul
          </Button>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-testid="auth-form">
          <div className="space-y-2">
            <Label className="text-stone-300" htmlFor="email" data-testid="auth-email-label">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formState.email}
              onChange={handleChange}
              placeholder="oracle@dracon.gg"
              className="bg-stone-950 border-stone-800 text-stone-200"
              data-testid="auth-email-input"
              required
            />
          </div>

          {mode === "register" && (
            <div className="space-y-2" data-testid="auth-username-field">
              <Label className="text-stone-300" htmlFor="username" data-testid="auth-username-label">
                Display Name
              </Label>
              <Input
                id="username"
                name="username"
                value={formState.username}
                onChange={handleChange}
                placeholder="Crimson Warden"
                className="bg-stone-950 border-stone-800 text-stone-200"
                data-testid="auth-username-input"
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-stone-300" htmlFor="password" data-testid="auth-password-label">
              Password
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formState.password}
              onChange={handleChange}
              placeholder="••••••••"
              className="bg-stone-950 border-stone-800 text-stone-200"
              data-testid="auth-password-input"
              required
            />
          </div>

          {statusMessage && (
            <div
              className="bg-red-900/30 border border-red-900/50 text-red-200 px-4 py-3 rounded-lg"
              data-testid="auth-status-message"
            >
              {statusMessage}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? "Sealing..." : mode === "login" ? "Enter" : "Bind"}
          </Button>
        </form>
      </div>
    </div>
  );
}
